import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ContractDetail, PaymentRow } from '@/lib/api';
import { fmtDate, fmtMoney, fmtPct } from '@/utils/format';

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  cash: 'Efectivo',
  transfer: 'Transferencia',
  deposit: 'Depósito',
  check: 'Cheque',
};

const BRAND = { primary: '#2952b3', dark: '#1d3a85', muted: '#5a6477', divider: '#c8cee0' };

function header(doc: jsPDF, title: string, subtitle: string) {
  doc.setFillColor(BRAND.primary);
  doc.rect(0, 0, 210, 22, 'F');
  doc.setTextColor('#ffffff');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('CrediFacilGT', 14, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(new Date().toLocaleDateString('es-GT'), 14, 16);

  doc.setTextColor('#1a1f2c');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, 14, 32);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(BRAND.muted);
  doc.text(subtitle, 14, 38);
  doc.setTextColor('#1a1f2c');
}

function dataPair(doc: jsPDF, x: number, y: number, label: string, value: string) {
  doc.setFontSize(8);
  doc.setTextColor(BRAND.muted);
  doc.text(label.toUpperCase(), x, y);
  doc.setFontSize(11);
  doc.setTextColor('#1a1f2c');
  doc.setFont('helvetica', 'bold');
  doc.text(value, x, y + 5);
  doc.setFont('helvetica', 'normal');
}

function footer(doc: jsPDF) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(BRAND.muted);
    doc.text('Plan de pagos generado por CrediFacilGT', 14, h - 8);
    doc.text(`Página ${i} de ${pages}`, 196, h - 8, { align: 'right' });
  }
}

const METHOD_LABEL = { french: 'Francés', german: 'Alemán' } as const;

export function renderStatement(doc: jsPDF, contract: ContractDetail, payments: PaymentRow[] = []) {
  header(
    doc,
    `Estado de cuenta · ${contract.contractNumber}`,
    `${contract.buyerName}${contract.buyerCode ? ` · ${contract.buyerCode}` : ''}`,
  );

  const isCash = contract.saleType === 'cash';

  doc.setDrawColor(BRAND.divider);
  doc.setFillColor('#f4f6fb');
  doc.roundedRect(14, 46, 182, 24, 2, 2, 'FD');
  if (isCash) {
    dataPair(doc, 18, 54, 'Tipo de venta', 'Pago de contado');
    dataPair(doc, 70, 54, 'Precio de venta', fmtMoney(contract.financedAmount));
    dataPair(doc, 130, 54, 'Fecha de venta', fmtDate(contract.startDate));
  } else {
    dataPair(doc, 18, 54, 'Monto financiado', fmtMoney(contract.financedAmount));
    dataPair(doc, 70, 54, 'Tasa anual', fmtPct(contract.interestRateAnnual));
    dataPair(doc, 112, 54, 'Plazo', `${contract.termMonths} meses`);
    dataPair(doc, 150, 54, 'Método', METHOD_LABEL[contract.method]);
    dataPair(doc, 18, 64, 'Inicio', fmtDate(contract.startDate));
    if (contract.schedule) {
      dataPair(doc, 70, 64, 'Total intereses', fmtMoney(contract.schedule.totalInterest));
      dataPair(doc, 122, 64, 'Total a pagar', fmtMoney(contract.schedule.totalPaid));
    }
  }

  let cursorY = 76;
  if (!isCash) {
    const showAdminFee = contract.installments.some((r) => Number(r.adminFeeAmount ?? '0') > 0);
    autoTable(doc, {
      startY: 76,
      head: [showAdminFee
        ? ['#', 'Fecha', 'Cuota', 'Capital', 'Interés', 'Costo adm.', 'Saldo']
        : ['#', 'Fecha', 'Cuota', 'Capital', 'Interés', 'Saldo']],
      body: contract.installments.map((r) =>
        showAdminFee
          ? [r.installmentNumber, fmtDate(r.dueDate), fmtMoney(r.totalAmount), fmtMoney(r.principalAmount), fmtMoney(r.interestAmount), fmtMoney(r.adminFeeAmount ?? '0.00'), fmtMoney(r.closingBalance)]
          : [r.installmentNumber, fmtDate(r.dueDate), fmtMoney(r.totalAmount), fmtMoney(r.principalAmount), fmtMoney(r.interestAmount), fmtMoney(r.closingBalance)],
      ),
      headStyles: { fillColor: BRAND.dark, textColor: '#ffffff', fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 1.5 },
      alternateRowStyles: { fillColor: '#f8f9fc' },
      margin: { left: 14, right: 14 },
    });
    cursorY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 76;
  }

  if (contract.balance) {
    cursorY += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#1a1f2c');
    doc.text(
      `Pagado: ${fmtMoney(contract.balance.totalPaid)}    Saldo pendiente: ${fmtMoney(contract.balance.outstanding)}`,
      14,
      cursorY,
    );
    const extras: string[] = [];
    if (Number(contract.balance.adminFeeOutstanding ?? '0') > 0) extras.push(`Costo adm. pendiente: ${fmtMoney(contract.balance.adminFeeOutstanding ?? '0')}`);
    if (Number(contract.balance.moraOutstanding ?? '0') > 0) extras.push(`Mora: ${fmtMoney(contract.balance.moraOutstanding ?? '0')}`);
    if (extras.length) {
      // One line each — joining with spaces can overflow the page margin at large amounts.
      doc.setFontSize(10);
      extras.forEach((e, i) => doc.text(e, 14, cursorY + 5 + i * 5));
      cursorY += 5 + extras.length * 5;
    }
    doc.setFont('helvetica', 'normal');
    cursorY += 4;
  }

  if (payments.length) {
    autoTable(doc, {
      startY: cursorY + 4,
      head: [['Fecha', 'Monto', 'Método', 'Referencia']],
      body: payments.map((p) => [
        fmtDate(p.paymentDate),
        fmtMoney(p.amount),
        PAYMENT_METHOD_LABEL[p.method] ?? p.method,
        p.reference ?? '—',
      ]),
      headStyles: { fillColor: BRAND.dark, textColor: '#ffffff', fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 1.5 },
      alternateRowStyles: { fillColor: '#f8f9fc' },
      margin: { left: 14, right: 14 },
    });
  }
}

export function exportContractStatementPDF(contract: ContractDetail, payments: PaymentRow[] = []) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  renderStatement(doc, contract, payments);
  footer(doc);
  doc.save(`EstadoCuenta_${contract.contractNumber}.pdf`);
}

/** One document, N statements, a page break between each, page numbers stamped once at the end. */
export function exportBulkStatementsPDF(
  items: { contract: ContractDetail; payments: PaymentRow[] }[],
  filename: string,
) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  items.forEach((item, i) => {
    if (i > 0) doc.addPage();
    renderStatement(doc, item.contract, item.payments);
  });
  footer(doc);
  doc.save(filename);
}
